import { AssistantCitation } from '@services/modules/AssistantStream'
import {
  Children,
  createContext,
  KeyboardEvent,
  ReactNode,
  useContext,
  useMemo,
} from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

import {
  CITATION_ID_ATTRIBUTE,
  CITATION_LINK_TAG,
  remarkCitationLinks,
} from './citationLinks'
import { citationsById } from './recordTokens'

/*
 * The one place text written by the model becomes markup. Markdown is restricted to a safe
 * subset: no raw HTML (react-markdown default), images are stripped and links render as plain
 * text — the FE must never follow or fetch a URL coming from model output (prompt-injection
 * exfiltration guard). Navigation only happens through server-built citations: the label of
 * one, where the text writes it, opens that citation's record (see citationLinks).
 */

interface CitationLinks {
  /** The citations the text may link to, by label. */
  byLabel: Map<string, AssistantCitation>
  /** Names of records the user may not open: marked in bold, not linked. */
  boldLabels: Set<string>
  /** The records the text names by ref, by citation id. */
  byId: Map<string, AssistantCitation>
  canOpen?: (_citation: AssistantCitation) => boolean
  open?: (_citation: AssistantCitation) => void
}

const CitationLinksContext = createContext<CitationLinks | null>(null)

/** The record a ref names: a link, bold when the user may not open it, else its name. */
const recordCitation = (links: CitationLinks | null, id: string | undefined) =>
  id ? links?.byId.get(id) : undefined

/** A citation's label in the text. Without its citation it is the text it was. */
const CitationLink = ({
  children,
  ...props
}: {
  children?: ReactNode
  [CITATION_ID_ATTRIBUTE]?: string
}) => {
  const links = useContext(CitationLinksContext)
  const label = Children.toArray(children).join('')
  const recordId = props[CITATION_ID_ATTRIBUTE]
  const byRef = recordCitation(links, recordId)
  if (recordId) {
    if (!byRef) {
      return <>{children}</>
    }
    if (byRef.openable === false) {
      return <strong>{children}</strong>
    }
    if (!links?.open || !(links.canOpen?.(byRef) ?? true)) {
      return <>{children}</>
    }
  }
  const citation = byRef ?? links?.byLabel.get(label)
  if (!links?.open || !citation) {
    return links?.boldLabels.has(label) ? (
      <strong>{children}</strong>
    ) : (
      <>{children}</>
    )
  }
  const open = links.open
  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      open(citation)
    }
  }
  // Not a button: Copy leaves buttons out of an answer, and this is a word of it.
  return (
    <span
      role="link"
      tabIndex={0}
      className="kw-cite"
      title="Open the record"
      onClick={() => open(citation)}
      onKeyDown={onKeyDown}
    >
      {children}
    </span>
  )
}

const markdownComponents = {
  img: () => null,
  a: ({ children }: any) => <span>{children}</span>,
  [CITATION_LINK_TAG]: CitationLink,
}

/** Second line of defence: no URL of the text survives, whatever element would carry it. */
const dropUrl = () => ''

interface Props {
  children: string
  /** Server citations whose labels, where the text writes them, open their record. */
  citations?: AssistantCitation[]
  /** Opens a citation's record; without it no label is a link. */
  onOpenCitation?: (_citation: AssistantCitation) => void
  /** Records the text names that the user may not open: their names are shown in bold. */
  namedOnly?: AssistantCitation[]
  /** The records the text names by ref (marked by markRecordTokens): all the answer's citations. */
  records?: AssistantCitation[]
  /** Whether the user can open a record (a ref to one they cannot is shown as its name). */
  canOpenCitation?: (_citation: AssistantCitation) => boolean
}

const AssistantMarkdown = ({
  children,
  citations,
  onOpenCitation,
  namedOnly,
  records,
  canOpenCitation,
}: Props) => {
  const linked = onOpenCitation ? citations ?? [] : []
  const bold = namedOnly ?? []
  const named = records ?? []
  const labels = [...linked, ...bold].map(c => c.label)
  const linksKey = [...linked, ...bold, ...named]
    .map(c => `${c.type}:${c.id}:${c.label}:${c.openable}`)
    .join('\n')
  const links = useMemo<CitationLinks | null>(
    () =>
      linked.length || bold.length || named.length
        ? {
            byLabel: new Map(linked.map(c => [c.label, c])),
            boldLabels: new Set(bold.map(c => c.label)),
            byId: citationsById(named),
            canOpen: canOpenCitation,
            open: onOpenCitation,
          }
        : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [linksKey, onOpenCitation, canOpenCitation]
  )
  const recordLabels = useMemo(
    () =>
      Object.fromEntries(
        Array.from(citationsById(named).entries()).map(([id, c]) => [
          id,
          c.label,
        ])
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [linksKey]
  )

  return (
    <CitationLinksContext.Provider value={links}>
      <ReactMarkdown
        remarkPlugins={[
          remarkGfm,
          [remarkCitationLinks, { labels, recordLabels }],
        ]}
        components={markdownComponents}
        urlTransform={dropUrl}
      >
        {children}
      </ReactMarkdown>
    </CitationLinksContext.Provider>
  )
}

export default AssistantMarkdown
